<script lang="ts">
const __DsBlock = {
	type: 'display',
	name: '$t:blocks.display_formatted_datetime.name',
	description: '$t:blocks.display_formatted_datetime.description',
	icon: 'lucide:calendar-clock',
	category: 'display',
	hidden: true,

	fieldBinding: {
		supportedTypes: supportedDateTimeFieldTypes,
		defaultProps: ({ typeOptions }) => ({
			defaultTimezone: (typeOptions as DatetimeFieldOptions | undefined)?.defaultTimezone,
		}),
	},

	options: ({ form, values, context }) => form([
		{
			name: 'value',
		},
		{
			name: 'displayMode',
			description: '$t:blocks.date_time_display_options.display_mode.description',
			block: '$builtin:InputSelect',
			type: 'enum',
			values: DATE_TIME_DISPLAY_MODE_VALUES,
			props: {
				options: DATE_TIME_DISPLAY_MODE_OPTIONS,
			},
		},
		...(values.displayMode === 'relative'
			? []
			: [{
					name: 'dateFormat',
					description: '$t:blocks.date_time_display_options.date_format.description',
					block: '$builtin:InputSelect',
					type: 'enum',
					values: DATE_TIME_DISPLAY_FORMAT_VALUES,
					props: {
						options: DATE_DISPLAY_FORMAT_OPTIONS,
					},
				}, {
					name: 'timeFormat',
					description: '$t:blocks.date_time_display_options.time_format.description',
					block: '$builtin:InputSelect',
					type: 'enum',
					values: TIME_DISPLAY_FORMAT_VALUES,
					props: {
						options: TIME_DISPLAY_FORMAT_OPTIONS,
					},
				}]),
		...getDefaultTimezoneFormFields(context?.primitiveType, { includeWhenPrimitiveTypeUnknown: true }),
	]),
}
</script>
<script setup lang="ts">
import type { DateTimeModelValue } from '#layers/blocks/blocks/Input/InputDateTime.block.vue';
import type { DateTimeDisplayFormat, DateTimeDisplayMode, TimeDisplayFormat } from '#layers/blocks/constants/date-time-display-options';
import type { DisplayBlockProps } from '#layers/blocks/types/display-block';
import type { DatetimeFieldOptions } from '#layers/data-model/public';
import { Temporal } from 'temporal-polyfill';
import { supportedDateTimeFieldTypes } from '#layers/blocks/blocks/Input/InputDateTime.block.vue';
import {
	DATE_DISPLAY_FORMAT_OPTIONS,
	DATE_TIME_DISPLAY_FORMAT_VALUES,
	DATE_TIME_DISPLAY_MODE_OPTIONS,
	DATE_TIME_DISPLAY_MODE_VALUES,
	DateTimeFormat,
	TIME_DISPLAY_FORMAT_OPTIONS,
	TIME_DISPLAY_FORMAT_VALUES,
} from '#layers/blocks/constants/date-time-display-options';
import { getDatetimePrimitiveSemantics, getDefaultTimezoneFormFields, getTypename, resolveDatetimeTimezone } from '#layers/data-model/public';

interface DisplayFormattedDatetimeProps extends DisplayBlockProps<DateTimeModelValue>, DatetimeFieldOptions {
	displayMode?: DateTimeDisplayMode;
	dateFormat?: DateTimeDisplayFormat;
	timeFormat?: TimeDisplayFormat;
	dateTimeFormat?: DateTimeFormat;
}

import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
	formatPlainDateTime,
	formatRelativePlainDateTime,
	formatRelativeTime,
	formatTechnicalDateTime,
	getPlainDateTimeFormatOptions,
	isEpochTimestampValue,
	isNil,
	isZonedDateTime,
	TIMEZONE_LOCAL,
	unixTimestampConverter,
	useUserLocale,
} from '#layers/base/public';
import { defineBlock } from '#layers/block-engine/public';
import { useTimezone } from '#layers/blocks/composables/use-timezone';
import TimestampHoverCard from './TimestampHoverCard.vue';

const props = withDefaults(defineProps<DisplayFormattedDatetimeProps>(), {
	displayMode: 'absolute',
	dateFormat: 'medium',
	timeFormat: 'short',
	dateTimeFormat: DateTimeFormat.Localized,
	defaultTimezone: TIMEZONE_LOCAL,
});

;

const primitiveTypeName = computed(() => props.primitiveType ? getTypename(props.primitiveType) : undefined);
const primitiveSemantics = computed(() => getDatetimePrimitiveSemantics(primitiveTypeName.value));

const isTimestamp = computed(() => {
	if (primitiveTypeName.value) return primitiveSemantics.value.isTimestamp;
	return isEpochTimestampValue(props.value);
});

const isTimezoneType = computed(() => {
	if (primitiveTypeName.value) return primitiveSemantics.value.isTimezoneType;
	return isZonedDateTime(props.value);
});

const isInstantType = computed(() => {
	if (primitiveTypeName.value) return primitiveSemantics.value.isInstantType;
	return isTimestamp.value || isTimezoneType.value;
});

const currentTimezone = computed(() => resolveDatetimeTimezone(isInstantType.value, props.defaultTimezone));

const instant = computed<Temporal.Instant | null>(() => {
	if (props.value == null) return null;

	if (isTimestamp.value && isEpochTimestampValue(props.value)) {
		return unixTimestampConverter.toTemporal(props.value);
	}

	if (isInstantType.value && isZonedDateTime(props.value)) {
		return props.value.toInstant();
	}

	return null;
});

const dateTime = computed<Temporal.PlainDateTime | null>(() => {
	if (props.value == null) return null;

	if (instant.value) {
		return instant.value.toZonedDateTimeISO(currentTimezone.value).toPlainDateTime();
	}

	if (isTimestamp.value) return null;

	if (isZonedDateTime(props.value)) {
		return props.value.toPlainDateTime();
	}

	if (props.value instanceof Temporal.PlainDateTime) {
		return props.value;
	}

	if (typeof props.value === 'string') {
		try {
			return Temporal.PlainDateTime.from(props.value);
		}
		catch {
			return null;
		}
	}

	return null;
});

const { formatLocale } = useUserLocale();

const { timezoneLabel, tooltipContent } = useTimezone({
	timezone: currentTimezone,
	dateTime,
	locale: formatLocale,
});

const formattedDatetime = computed(() => {
	if (!dateTime.value) return;

	if (props.dateTimeFormat === DateTimeFormat.Technical) {
		return formatTechnicalDateTime(dateTime.value);
	}

	return formatPlainDateTime(dateTime.value, formatLocale.value, getPlainDateTimeFormatOptions({
		dateStyle: props.dateFormat,
		timeStyle: props.timeFormat,
	}));
});
const isTechnicalFormat = computed(() => props.dateTimeFormat === DateTimeFormat.Technical);
const generalFormattedDatetime = computed(() => formatPlainDateTime(dateTime.value, formatLocale.value));
const relativeTooltipContent = computed(() => {
	if (!generalFormattedDatetime.value) return;

	if (isInstantType.value && timezoneLabel.value && tooltipContent.value) {
		return `${generalFormattedDatetime.value} ${timezoneLabel.value} (${tooltipContent.value})`;
	}

	return generalFormattedDatetime.value;
});

const i18n = useI18n();

const relativeDatetime = computed(() => {
	if (instant.value) {
		return formatRelativeTime(instant.value, i18n, { locale: formatLocale.value, granularity: 'minute' });
	}

	return dateTime.value
		? formatRelativePlainDateTime(dateTime.value, i18n, { locale: formatLocale.value, granularity: 'minute' })
		: undefined;
});
</script>

<template>
	<DDisplayValueEmpty v-if="isNil(value)" />
	<template v-else-if="displayMode === 'relative'">
		<TimestampHoverCard v-if="instant" :instant include-screen-reader-description>
			<span class="underline decoration-(--ui-border-accented) decoration-dotted underline-offset-2">
				{{ relativeDatetime }}
			</span>
		</TimestampHoverCard>
		<DTooltip v-else :delay-duration="0" :text="relativeTooltipContent">
			<span>{{ relativeDatetime }}</span>
		</DTooltip>
	</template>
	<TimestampHoverCard v-else-if="instant" :instant>
		<span
			class="
				inline-flex max-w-full min-w-0 items-baseline gap-1 overflow-hidden align-bottom whitespace-nowrap underline
				decoration-(--ui-border-accented) decoration-dotted underline-offset-2
			"
		>
			<span class="min-w-0 truncate whitespace-nowrap" :class="{ 'font-mono font-light': isTechnicalFormat }">
				{{ formattedDatetime }}
			</span>
			<span
				v-if="isTimezoneType && timezoneLabel"
				class="flex-none whitespace-nowrap text-dimmed"
			>
				{{ timezoneLabel }}
			</span>
		</span>
	</TimestampHoverCard>
	<span
		v-else
		class="inline-flex max-w-full min-w-0 items-baseline gap-1 overflow-hidden align-bottom whitespace-nowrap"
	>
		<span class="min-w-0 truncate whitespace-nowrap" :class="{ 'font-mono font-light': isTechnicalFormat }">
			{{ formattedDatetime }}
		</span>
		<DTooltip
			v-if="isTimezoneType && tooltipContent"
			:delay-duration="0"
			:text="tooltipContent"
		>
			<span class="flex-none whitespace-nowrap text-dimmed">{{ timezoneLabel }}</span>
		</DTooltip>
	</span>
</template>
